/**
 * Growing Guide page behaviour.
 *
 * Four independent custom elements:
 *   <gg-tabs>   — the Hydroponic Kit Instructions tab strip.
 *   <gg-video>  — one video card plus its <dialog> lightbox.
 *   <gg-videos> — the video grid's "show all" reveal.
 *   <gg-guides> — client-side search / filter / "view more" for the plant guide grid.
 *
 * All four are progressive enhancement: with JS disabled the first instruction panel
 * still renders, video thumbnails stay in place, and every guide card is visible.
 */

/** Defines a custom element only once, so theme editor re-renders don't throw. */
const defineOnce = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

/** Lowercases, drops accents, and reduces punctuation to spaces so search is forgiving. */
const normalize = (value) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

class GrowingGuideTabs extends HTMLElement {
  #controller = new AbortController();

  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  connectedCallback() {
    // A fresh controller each time, so the element still works if it is moved in the DOM.
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener(
      'click',
      (event) => {
        const tab = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
        if (tab instanceof HTMLElement && this.contains(tab)) this.#activate(tab);
      },
      { signal }
    );

    this.addEventListener('keydown', this.#onKeydown, { signal });

    // The editor scrolls to a selected block, but inactive panels are hidden — reveal it.
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
  #onBlockSelect = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const panel = target.closest('[role="tabpanel"]');
    if (!panel || !this.contains(panel)) return;

    const tab = this.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`);
    if (tab instanceof HTMLElement) this.#activate(tab);
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const tabs = this.#tabs;
    const current = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (current === -1) return;

    const offsets = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let next = -1;

    if (event.key in offsets) next = (current + offsets[event.key] + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    this.#activate(tabs[next]);
    tabs[next].focus();
  };

  /** @param {HTMLElement} active */
  #activate(active) {
    for (const tab of this.#tabs) {
      const isActive = tab === active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      tab.classList.toggle('is-active', isActive);

      const panelId = tab.getAttribute('aria-controls');
      if (!panelId) continue;

      const panel = this.querySelector(`#${CSS.escape(panelId)}`);
      if (panel instanceof HTMLElement) panel.hidden = !isActive;
    }
  }
}

class GrowingGuideVideo extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.querySelector('[data-gg-video-open]')?.addEventListener(
      'click',
      () => {
        const iframe = dialog.querySelector('iframe[data-gg-embed]');
        if (iframe instanceof HTMLIFrameElement && !iframe.src && iframe.dataset.ggEmbed) {
          iframe.src = iframe.dataset.ggEmbed;
        }

        dialog.showModal();
      },
      { signal }
    );

    dialog.querySelector('[data-gg-video-close]')?.addEventListener('click', () => dialog.close(), { signal });

    // The dialog element itself is the backdrop; its inner box swallows its own clicks.
    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) dialog.close();
      },
      { signal }
    );

    // Tearing down the player is what actually stops playback behind the closed dialog.
    dialog.addEventListener(
      'close',
      () => {
        const iframe = dialog.querySelector('iframe[data-gg-embed]');
        if (iframe instanceof HTMLIFrameElement) iframe.removeAttribute('src');
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}
/** Reveals the video cards the section held back past its visible count. */
class GrowingGuideVideos extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();
    const wrapper = this.querySelector('[data-gg-more-wrapper]');
    if (!(wrapper instanceof HTMLElement)) return;

    wrapper.querySelector('[data-gg-more]')?.addEventListener(
      'click',
      () => {
        for (const card of this.querySelectorAll('[data-gg-video-item][hidden]')) {
          if (card instanceof HTMLElement) card.hidden = false;
        }

        // Nothing left to reveal, so the button retires rather than sitting there inert.
        wrapper.hidden = true;
      },
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}

/**
 * @typedef {object} GuideCard
 * @property {HTMLElement} element
 * @property {string} title - Lowercased title, used as the sort key.
 * @property {string} categories - Pipe-delimited handles, e.g. "|herb|".
 * @property {string} haystack - Normalized text searched against.
 */

/** Fixed display order: title A–Z. The sort dropdown was removed by design. */
const byTitleAsc = (a, b) => a.title.localeCompare(b.title);

/** Query string keys, kept prefixed so they never collide with Shopify's own params. */
const PARAM = {
  query: 'guide_q',
  category: 'guide_category',
};

class GrowingGuidePlantGuides extends HTMLElement {
  #controller = new AbortController();
  /** @type {GuideCard[]} */
  #cards = [];
  #batch = 8;
  #visible = 8;
  #searchTimer = 0;
  #moreLabel = '';
  #lessLabel = '';
  #state = { query: '', category: '' };
  #refs = {
    /** @type {HTMLInputElement | null} */ search: null,
    /** @type {HTMLElement | null} */ empty: null,
    /** @type {HTMLElement | null} */ more: null,
    /** @type {HTMLElement | null} */ moreLabel: null,
    /** @type {HTMLElement | null} */ count: null,
  };

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#batch = Number(this.dataset.ggBatch) || 8;
    this.#visible = this.#batch;

    this.#refs = {
      search: this.querySelector('[data-gg-search]'),
      empty: this.querySelector('[data-gg-empty]'),
      more: this.querySelector('[data-gg-more-wrapper]'),
      moreLabel: this.querySelector('[data-gg-more-label]'),
      count: this.querySelector('[data-gg-count]'),
    };
    this.#moreLabel = this.#refs.moreLabel?.textContent?.trim() || '';
    this.#lessLabel = this.dataset.ggLessLabel || '';

    this.#cards = [...this.querySelectorAll('[data-gg-card]')].map((element) => ({
      element: /** @type {HTMLElement} */ (element),
      title: element.getAttribute('data-gg-title') ?? '',
      categories: element.getAttribute('data-gg-category') ?? '',
      haystack: normalize(element.getAttribute('data-gg-search') ?? ''),
    }));

    this.#readFromUrl();
    this.#syncControls();

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
    clearTimeout(this.#searchTimer);
  }

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const chip = target.closest('[data-gg-filter="category"]');
    if (chip instanceof HTMLElement) {
      this.#state.category = chip.getAttribute('data-gg-value') ?? '';
      this.#resetPaging();
      return;
    }

    const moreButton = target.closest('[data-gg-more]');
    if (moreButton instanceof HTMLElement) {
      // Once expanded, the button acts as "view less" and collapses to the first page.
      const isExpanded = moreButton.getAttribute('data-gg-more-expanded') === 'true';
      this.#visible = isExpanded ? this.#batch : this.#visible + this.#batch;
      this.#render();
    }
  };
  #onInput = (/** @type {Event} */ event) => {
    if (event.target !== this.#refs.search) return;

    clearTimeout(this.#searchTimer);
    this.#searchTimer = setTimeout(() => {
      this.#state.query = this.#refs.search?.value ?? '';
      this.#visible = this.#batch;
      this.#render();
    }, 150);
  };

  /** Re-syncs every control then renders from page one. */
  #resetPaging() {
    this.#visible = this.#batch;
    this.#syncControls();
    this.#render();
  }

  #syncControls() {
    const { query, category } = this.#state;

    if (this.#refs.search && this.#refs.search.value !== query) this.#refs.search.value = query;

    for (const chip of this.querySelectorAll('[data-gg-filter="category"]')) {
      const isActive = (chip.getAttribute('data-gg-value') ?? '') === category;
      chip.classList.toggle('is-active', isActive);
      chip.setAttribute('aria-pressed', String(isActive));
    }
  }
  #render() {
    const { query, category } = this.#state;
    const terms = normalize(query).split(' ').filter(Boolean);

    const matches = this.#cards.filter((card) => {
      if (category && !card.categories.includes(`|${category}|`)) return false;
      return terms.every((term) => card.haystack.includes(term));
    });

    matches.sort(byTitleAsc);

    // Ordering via CSS `order` keeps the grid stable — no DOM moves, no reflow storm.
    for (const card of this.#cards) card.element.hidden = true;

    matches.forEach((card, index) => {
      card.element.hidden = index >= this.#visible;
      card.element.style.order = String(index);
    });

    this.#refs.empty?.toggleAttribute('hidden', matches.length > 0);

    if (this.#refs.more) {
      const hasMore = matches.length > this.#visible;
      // With a "view less" label the button toggles between paging forward and
      // collapsing; without one it hides once everything is visible (legacy).
      const hideToggle = this.#lessLabel === '' ? !hasMore : matches.length <= this.#batch;
      this.#refs.more.toggleAttribute('hidden', hideToggle);
      this.#refs.more
        .querySelector('[data-gg-more]')
        ?.setAttribute('data-gg-more-expanded', String(!hasMore && !hideToggle));
      if (this.#refs.moreLabel) {
        this.#refs.moreLabel.textContent = hasMore || !this.#lessLabel ? this.#moreLabel : this.#lessLabel;
      }
    }

    if (this.#refs.count) {
      const noun = matches.length === 1 ? 'plant guide' : 'plant guides';
      this.#refs.count.textContent = `${matches.length} ${noun} found`;
    }

    this.#writeToUrl();
  }

  #readFromUrl() {
    const params = new URLSearchParams(window.location.search);

    this.#state.query = params.get(PARAM.query) ?? '';
    this.#state.category = params.get(PARAM.category) ?? '';
  }

  /** Keeps filters shareable and survivable across a browser reload. */
  #writeToUrl() {
    if (window.Shopify?.designMode) return;

    const params = new URLSearchParams(window.location.search);

    for (const [key, value] of /** @type {[keyof typeof PARAM, string][]} */ (Object.entries(this.#state))) {
      if (value === '') params.delete(PARAM[key]);
      else params.set(PARAM[key], value);
    }

    // Retires the pre-removal sort param so old shared links clean themselves up.
    params.delete('guide_sort');

    const search = params.toString();
    const next = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;

    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(window.history.state, '', next);
    }
  }
}

class GrowingGuideDetail extends HTMLElement {
  #controller = new AbortController();
  #observer;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const links = [...this.querySelectorAll('[data-gg-detail-link]')];
    const sections = [...this.querySelectorAll('[data-gg-detail-section]')];

    for (const stage of this.querySelectorAll('[data-gg-stage]')) {
      if (stage instanceof HTMLElement) this.#initStageSwitcher(stage, signal);
    }

    if (!links.length || !sections.length) return;

    const activate = (id) => {
      for (const link of links) {
        const isActive = link.getAttribute('href') === `#${id}`;
        link.classList.toggle('is-active', isActive);
        if (isActive) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      }
    };

    const hashId = window.location.hash.slice(1);
    activate(sections.some((section) => section.id === hashId) ? hashId : sections[0].id);

    this.#observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target instanceof HTMLElement) activate(visible.target.id);
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: [0, 0.1, 0.5] }
    );

    for (const section of sections) this.#observer.observe(section);
    window.addEventListener('hashchange', () => activate(window.location.hash.slice(1)), {
      signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }

  /**
   * Selectable growth-stage tabs: clicking (or arrow-keying through) a stage
   * image highlights it and swaps the care-instruction panel below.
   * @param {HTMLElement} root
   * @param {AbortSignal} signal
   */
  #initStageSwitcher(root, signal) {
    const tabs = [...root.querySelectorAll('[data-gg-stage-tab]')];
    const panels = [...root.querySelectorAll('[data-gg-stage-panel]')];
    const options = [...root.querySelectorAll('[data-gg-stage-option]')];
    const prevBtns = [...root.querySelectorAll('[data-gg-stage-prev]')];
    const nextBtns = [...root.querySelectorAll('[data-gg-stage-next]')];
    const current = root.querySelector('[data-gg-stage-current]');
    if (!tabs.length || !panels.length) return;

    let index = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    const select = (nextIndex, moveFocus = false, scroll = true) => {
      index = (nextIndex + tabs.length) % tabs.length;

      tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      for (const option of options) {
        option.classList.toggle(
          'is-active',
          Number(option.getAttribute('data-gg-stage-option')) === index
        );
      }

      for (const panel of panels) {
        const isActive = Number(panel.getAttribute('data-gg-stage-panel')) === index;
        panel.classList.toggle('is-active', isActive);
        panel.toggleAttribute('hidden', !isActive);
      }

      if (current) current.textContent = String(index + 1);
      for (const btn of prevBtns) {
        if (btn instanceof HTMLButtonElement) btn.disabled = index === 0;
      }
      for (const btn of nextBtns) {
        if (btn instanceof HTMLButtonElement) btn.disabled = index === tabs.length - 1;
      }

      const activeTab = tabs[index];
      if (activeTab instanceof HTMLElement) {
        if (scroll) {
          activeTab.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        }
        if (moveFocus) activeTab.focus({ preventScroll: true });
      }
    };

    root.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const tab = target.closest('[data-gg-stage-tab]');
        if (tab instanceof HTMLElement && root.contains(tab)) {
          select(Number(tab.getAttribute('data-gg-stage-tab')));
          return;
        }

        if (target.closest('[data-gg-stage-prev]')) select(index - 1);
        else if (target.closest('[data-gg-stage-next]')) select(index + 1);
      },
      { signal }
    );

    root.addEventListener(
      'keydown',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest('[data-gg-stage-tab]')) return;

        let nextIndex = -1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = index + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = index - 1;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = tabs.length - 1;
        else return;

        event.preventDefault();
        select(nextIndex, true);
      },
      { signal }
    );

    select(index, false, false);
  }
}

defineOnce('gg-tabs', GrowingGuideTabs);
defineOnce('gg-video', GrowingGuideVideo);
defineOnce('gg-videos', GrowingGuideVideos);
defineOnce('gg-guides', GrowingGuidePlantGuides);
defineOnce('gg-detail', GrowingGuideDetail);
